import { Skeleton } from "../../ui/skeleton";
import { formatUsd } from "../formatters";

const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

const formatPercentValue = (value, fallback = "N/A") => {
  if (!isFiniteNumber(value)) {
    return fallback;
  }

  return `${value.toFixed(2)}%`;
};

const formatBpsValue = (value, fallback = "N/A") => {
  if (!isFiniteNumber(value)) {
    return fallback;
  }

  return `${value.toFixed(1)} bps`;
};

const formatWholeNumber = (value, fallback = "0") => {
  if (!isFiniteNumber(value)) {
    return fallback;
  }

  return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

const InfoDot = ({ text }) => (
  <span className="reddicks-info-dot" title={text} aria-label={text}>
    i
  </span>
);

const getDeltaTone = (value) => {
  if (!isFiniteNumber(value)) {
    return "muted";
  }

  if (value > 0) {
    return "up";
  }

  if (value < 0) {
    return "down";
  }

  return "flat";
};

const getUtilizationTone = (value) => {
  if (!isFiniteNumber(value)) {
    return "muted";
  }

  if (value >= 8) {
    return "up";
  }

  if (value >= 3) {
    return "flat";
  }

  return "down";
};

const KpiChip = ({ label, value, tone = "muted", hint = "" }) => (
  <div className={`reddicks-kpi-chip reddicks-kpi-chip-${tone}`}>
    <span className="reddicks-kpi-label">
      <span>{label}</span>
      {hint ? <InfoDot text={hint} /> : null}
    </span>
    <strong className="reddicks-kpi-value">{value}</strong>
  </div>
);

const KpiSkeletonRow = ({ count }) => (
  <div className="reddicks-kpi-grid">
    {Array.from({ length: count }).map((_, index) => (
      <div className="reddicks-kpi-chip" key={`kpi-skeleton-${index}`}>
        <Skeleton className="h-3.5 w-24" />
        <Skeleton className="h-5 w-20 mt-2" />
      </div>
    ))}
  </div>
);

const ReddicksKpiStrip = ({ kpis, isLoading, hasCachedData }) => {
  const shouldShowLoading = isLoading && !hasCachedData;
  const marketShare = kpis?.marketShare || {};
  const flowSevenDay = kpis?.flow?.sevenDay || {};

  return (
    <div className="reddicks-kpi-shell">
      <div className="reddicks-kpi-group">
        <p className="reddicks-kpi-title">
          <span>Router Share and Efficiency</span>
          <InfoDot text="Share compares official RSwap against all routers in analytics totals. Effective fee rate is total fees divided by total volume on the official router." />
        </p>
        {shouldShowLoading ? (
          <KpiSkeletonRow count={3} />
        ) : (
          <div className="reddicks-kpi-grid">
            <KpiChip
              label="Router Volume Share"
              value={formatPercentValue(marketShare?.officialVolumeSharePct)}
              hint="Percent of all tracked router cumulative volume that belongs to official RSwap."
            />
            <KpiChip
              label="Router Fees Share"
              value={formatPercentValue(marketShare?.officialFeesSharePct)}
              hint="Percent of all tracked router cumulative fees that belongs to official RSwap."
            />
            <KpiChip
              label="Effective Fee Rate"
              value={formatPercentValue(marketShare?.effectiveFeeRatePct)}
              hint="Official router cumulative fees divided by cumulative volume."
            />
          </div>
        )}
      </div>

      <div className="reddicks-kpi-group">
        <p className="reddicks-kpi-title">
          <span>Flow and Liquidity Health (7D)</span>
          <InfoDot text="Seven-day rolling KPIs built from snapshot rows: averages, fee capture efficiency, and TVL stability range." />
        </p>
        {shouldShowLoading ? (
          <KpiSkeletonRow count={6} />
        ) : (
          <div className="reddicks-kpi-grid reddicks-kpi-grid-deltas">
            <KpiChip
              label="Avg TVL (7D)"
              value={formatUsd(flowSevenDay?.avgTvlUsd, { compact: true, fallback: "N/A" })}
              hint="Average snapshot TVL over the last seven days."
            />
            <KpiChip
              label="Avg Volume / Day"
              value={formatUsd(flowSevenDay?.avgVolumeUsd, { compact: true, fallback: "N/A" })}
              tone={getDeltaTone(flowSevenDay?.avgVolumeUsd)}
              hint="Average daily snapshot volume across the seven-day window."
            />
            <KpiChip
              label="Avg Fees / Day"
              value={formatUsd(flowSevenDay?.avgFeesUsd, { compact: true, fallback: "N/A" })}
              tone={getDeltaTone(flowSevenDay?.avgFeesUsd)}
              hint="Average daily snapshot fees across the seven-day window."
            />
            <KpiChip
              label="Volume / TVL"
              value={formatPercentValue(flowSevenDay?.volumeToTvlPct)}
              tone={getUtilizationTone(flowSevenDay?.volumeToTvlPct)}
              hint="Average daily volume divided by average TVL in the same seven-day window."
            />
            <KpiChip
              label="Fee Rate (7D)"
              value={formatBpsValue(flowSevenDay?.feeRateBps)}
              hint="Total fees divided by total volume over the seven-day window."
            />
            <KpiChip
              label="Snapshots (7D)"
              value={formatWholeNumber(flowSevenDay?.sampleCount, "0")}
              tone={getDeltaTone(flowSevenDay?.sampleCount)}
              hint="Number of analytics snapshots included in the seven-day calculations."
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReddicksKpiStrip;
